import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'
import FadeIn from '@/components/generals/fade-in'
import LineReveal from '@/components/generals/line-reveal'
import TextReveal from '@/components/generals/text-reveal'

interface PageHeroProps {
    chapter: string
    title: Array<ReactNode>
    intro?: string
    className?: string
    titleClassName?: string
    glowClassName?: string
    background?: ReactNode
    children?: ReactNode
}

const PageHero = ({
    chapter,
    title,
    intro,
    className,
    titleClassName,
    glowClassName,
    background,
    children
}: PageHeroProps): ReactNode => {
    return (
        <section id='top' className={cn('relative overflow-hidden', className)}>
            {background}

            <div
                aria-hidden='true'
                className={cn(
                    'pointer-events-none absolute inset-0 bg-[radial-gradient(60%_55%_at_88%_100%,rgb(201_171_124/0.09),transparent_70%)]',
                    glowClassName
                )}
            />

            <div className='shell relative flex min-h-[78svh] flex-col justify-end pb-16 pt-36 md:min-h-[86svh] md:pb-24 md:pt-44'>
                <FadeIn trigger='mount' delay={0.15}>
                    <p className='caps flex items-center gap-4 text-brass'>
                        <span>Chapitre {chapter}</span>
                    </p>
                </FadeIn>

                <LineReveal
                    as='h1'
                    trigger='mount'
                    delay={0.3}
                    stagger={0.14}
                    lines={title}
                    className={cn(
                        'mt-8 font-heading text-[clamp(3.9rem,13vw,12.5rem)] font-normal leading-[0.86] tracking-[-0.035em]',
                        titleClassName
                    )}
                />

                {intro && (
                    <TextReveal
                        trigger='mount'
                        delay={0.75}
                        text={intro}
                        className='mt-10 max-w-xl text-lg leading-relaxed text-ivory/70 md:text-xl md:leading-relaxed'
                    />
                )}

                {children}
            </div>
        </section>
    )
}

export default PageHero
